import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
export function QuestionnairePage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const inputClasses = 'w-full bg-transparent border-b border-[#595959] focus:border-[#A07656] py-3 text-[#F5F3F0] placeholder-[#595959] outline-none transition-colors rounded-none appearance-none';
  const labelClasses = 'block text-xs font-bold uppercase tracking-widest text-[#A07656] mb-1';
  return <div className="min-h-screen flex flex-col lg:flex-row bg-[#1A1A1A] font-sans selection:bg-[#A07656] selection:text-white" data-id="element-400">
      {/* Left Side - Image */}
      <div className="relative w-full lg:w-5/12 xl:w-1/2 h-64 lg:h-screen lg:sticky lg:top-0 overflow-hidden" data-id="element-401">
        <img src="https://cdn.magicpatterns.com/uploads/sTtRJm7yMtnZ8fERenRZ6C/0N8A8650.jpg" alt="Vaulted great room with stone fireplace" className="w-full h-full object-cover" data-id="element-402" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1A1A1A]" data-id="element-403" />

        {/* Logo */}
        <Link to="/" className="absolute top-6 left-6 lg:top-10 lg:left-10 z-10" data-id="element-404">
          <img src="https://cdn.magicpatterns.com/uploads/rsVSuidRepyz9oxNYVg3rK/Iron_Ridge_Logo.png" alt="Iron Ridge Custom Homes" className="h-12 lg:h-16 w-auto brightness-0 invert drop-shadow-md" data-id="element-405" />
        </Link>
      </div>

      {/* Right Side - Form / Thank You */}
      <div className="w-full lg:w-7/12 xl:w-1/2 min-h-screen px-6 py-12 lg:px-16 lg:py-20 flex flex-col justify-center relative z-10 bg-[#1A1A1A]" data-id="element-406">
        <Link to="/" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest font-medium text-[#595959] hover:text-[#A07656] transition-colors mb-12 w-fit" data-id="element-407">
          <ArrowLeft className="w-4 h-4" data-id="element-408" />
          Back to Home
        </Link>

        <AnimatePresence mode="wait" data-id="element-409">
          {!isSubmitted ? <motion.div key="form" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.5
        }} className="max-w-xl" data-id="element-410">
              <div className="mb-12" data-id="element-411">
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#F5F3F0] mb-4 leading-tight" data-id="element-412">
                  Let's Build Your Vision
                </h1>
                <p className="text-[#D4CFC8]/70 text-lg leading-relaxed" data-id="element-413">
                  Building a custom home is a deeply personal journey. To ensure
                  we can dedicate the time and attention your project deserves,
                  please tell us a little bit about what you have in mind.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-12" data-id="element-414">
                {/* Section 1: The Basics */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="space-y-6" data-id="element-415">
                  <h2 className="text-xl font-serif font-semibold text-[#F5F3F0] border-b border-[#252525] pb-2" data-id="element-416">
                    The Basics
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-417">
                    <div data-id="element-418">
                      <label className={labelClasses} data-id="element-419">First & Last Name</label>
                      <input type="text" required className={inputClasses} placeholder="John Doe" data-id="element-420" />
                    </div>
                    <div data-id="element-421">
                      <label className={labelClasses} data-id="element-422">Phone Number</label>
                      <input type="tel" required className={inputClasses} placeholder="(555) 123-4567" data-id="element-423" />
                    </div>
                    <div className="md:col-span-2" data-id="element-424">
                      <label className={labelClasses} data-id="element-425">Email Address</label>
                      <input type="email" required className={inputClasses} placeholder="john@example.com" data-id="element-426" />
                    </div>
                  </div>
                </motion.div>

                {/* Section 2: Project Location */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="space-y-6" data-id="element-427">
                  <h2 className="text-xl font-serif font-semibold text-[#F5F3F0] border-b border-[#252525] pb-2" data-id="element-428">
                    Project Location
                  </h2>
                  <div className="space-y-6" data-id="element-429">
                    <div data-id="element-430">
                      <label className={labelClasses} data-id="element-431">
                        Where are you looking to build?
                      </label>
                      <input type="text" required className={inputClasses} placeholder="City or Neighborhood" data-id="element-432" />
                    </div>
                    <div data-id="element-433">
                      <label className={labelClasses} data-id="element-434">
                        Do you currently own a lot?
                      </label>
                      <div className="relative" data-id="element-435">
                        <select required className={`${inputClasses} cursor-pointer`} data-id="element-436">
                          <option value="" disabled selected className="text-[#595959]" data-id="element-437">
                            Select an option...
                          </option>
                          <option value="yes" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-438">
                            Yes, I own a lot
                          </option>
                          <option value="looking" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-439">
                            No, I am currently looking
                          </option>
                          <option value="need-help" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-440">
                            I need help finding one
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Section 3: Timeline & Budget */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="space-y-6" data-id="element-441">
                  <h2 className="text-xl font-serif font-semibold text-[#F5F3F0] border-b border-[#252525] pb-2" data-id="element-442">
                    Timeline & Budget
                  </h2>
                  <div className="space-y-6" data-id="element-443">
                    <div data-id="element-444">
                      <label className={labelClasses} data-id="element-445">
                        When are you hoping to break ground?
                      </label>
                      <select required className={`${inputClasses} cursor-pointer`} data-id="element-446">
                        <option value="" disabled selected data-id="element-447">
                          Select timeline...
                        </option>
                        <option value="asap" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-448">
                          ASAP
                        </option>
                        <option value="3-6" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-449">
                          3-6 Months
                        </option>
                        <option value="6-12" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-450">
                          6-12 Months
                        </option>
                        <option value="dreaming" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-451">
                          Just dreaming right now
                        </option>
                      </select>
                    </div>
                    <div data-id="element-452">
                      <label className={labelClasses} data-id="element-453">
                        Anticipated budget range (Excluding land)
                      </label>
                      <select required className={`${inputClasses} cursor-pointer`} data-id="element-454">
                        <option value="" disabled selected data-id="element-455">
                          Select budget...
                        </option>
                        <option value="750-1m" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-456">
                          $750k - $1M
                        </option>
                        <option value="1m-1.5m" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-457">
                          $1M - $1.5M
                        </option>
                        <option value="1.5m+" className="bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-458">
                          $1.5M+
                        </option>
                      </select>
                    </div>
                  </div>
                </motion.div>

                {/* Section 4: The Vision */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="space-y-6" data-id="element-459">
                  <h2 className="text-xl font-serif font-semibold text-[#F5F3F0] border-b border-[#252525] pb-2" data-id="element-460">
                    The Vision
                  </h2>
                  <div data-id="element-461">
                    <label className={labelClasses} data-id="element-462">
                      Tell us briefly about your dream home.
                    </label>
                    <textarea required rows={4} className={`${inputClasses} resize-none`} placeholder="Architectural style, must-have features, or how you plan to use the space..." data-id="element-463" />
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="pt-4" data-id="element-464">
                  <button type="submit" className="w-full group relative inline-flex items-center justify-center px-10 py-5 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A] hover:shadow-[0_0_30px_rgba(160,118,86,0.3)]" data-id="element-465">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-466" />
                    <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-467">
                      Submit Details
                    </span>
                  </button>
                </motion.div>
              </form>
            </motion.div> : <motion.div key="thank-you" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl w-full" data-id="element-468">
              <div className="mb-10" data-id="element-469">
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#F5F3F0] mb-6 leading-tight" data-id="element-470">
                  Thank you for sharing your vision with us!
                </h1>
                <p className="text-[#D4CFC8]/80 text-lg leading-relaxed mb-8" data-id="element-471">
                  Based on your details, we'd love to invite you to a
                  complimentary Discovery Call. Please choose a time on our
                  calendar below that works for you.
                </p>

                <a href="https://calendar.app.google/5bvWP7BUZxFVtkSy8" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#A07656] text-white hover:bg-[#B8896A] transition-colors text-sm uppercase tracking-widest font-semibold" data-id="element-472">
                  Open Calendar to Book Appointment{' '}
                  <ExternalLink className="w-4 h-4" data-id="element-473" />
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}